import React from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  FirstPage, 
  LastPage 
} from '@mui/icons-material';
import { IconButton, Select, MenuItem, FormControl } from '@mui/material';
import './Pagination.css';

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  totalCount, // Alternative name for totalItems
  itemsPerPage,
  rowsPerPage, // Alternative name for itemsPerPage
  startIndex, // Optional - will be calculated if not provided
  onPageChange,
  onItemsPerPageChange,
  onRowsPerPageChange, // Alternative name for onItemsPerPageChange
  pageSizeOptions = [10, 25, 50, 100]
}) => {
  // Support both naming conventions
  const items = totalItems || totalCount || 0;
  const perPage = itemsPerPage || rowsPerPage || 10;
  const handlePerPageChange = onItemsPerPageChange || onRowsPerPageChange;
  
  const startItem = startIndex !== undefined ? startIndex + 1 : (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, items);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        <span>
          Showing {startItem} to {endItem} of {items} entries
        </span>
      </div>
      
      <div className="pagination-controls">
        <div className="page-size-selector">
          <span>Show</span>
          <FormControl size="small" className="page-size-select">
            <Select
              value={perPage}
              onChange={(e) => handlePerPageChange(e.target.value)}
            >
              {pageSizeOptions.map(size => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <span>entries</span>
        </div>

        {totalPages > 1 && (
          <div className="pagination-buttons">
            <IconButton
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              size="small"
              title="First Page"
            >
              <FirstPage />
            </IconButton>
            
            <IconButton
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              size="small"
              title="Previous Page"
            >
              <ChevronLeft />
            </IconButton>

            <div className="page-numbers">
              {getVisiblePages().map((page, index) => (
                <button
                  key={index}
                  className={`page-number ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
                  onClick={() => typeof page === 'number' && onPageChange(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}
            </div>

            <IconButton
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              size="small"
              title="Next Page"
            >
              <ChevronRight />
            </IconButton>
            
            <IconButton
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              size="small"
              title="Last Page"
            >
              <LastPage />
            </IconButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;